import React, { useState, useEffect } from 'react'
import { format, subHours, subDays } from 'date-fns'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
} from 'chart.js'
import 'chartjs-adapter-date-fns'
import zoomPlugin from 'chartjs-plugin-zoom'
import LiveChart from './LiveChart'
import LiveImportModal from './LiveImportModal'
import LiveReadingsManagementTable from './LiveReadingsManagementTable'

ChartJS.register(
  CategoryScale,
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  zoomPlugin
)

interface InfrastructureObject {
  id: number
  name: string
  infrastructure_type: string
  location?: string
  readings_count: number
  last_reading_at?: string
}

interface LiveReading {
  id: number
  object_id: number
  object_name: string
  infrastructure_type: string
  reading_type: string
  reading_type_label: string
  value: number
  timestamp: string
}

interface Anomaly extends LiveReading {
  deviation: number
  trend: 'above' | 'below'
}

interface LiveStatisticsPageProps {
  token: string
}

export default function LiveStatisticsPage({ token }: LiveStatisticsPageProps) {
  const [objects, setObjects] = useState<InfrastructureObject[]>([])
  const [selectedObjects, setSelectedObjects] = useState<number[]>([])
  const [liveReadings, setLiveReadings] = useState<LiveReading[]>([])
  const [loading, setLoading] = useState(true)
  const [startTime, setStartTime] = useState<Date>(() => subHours(new Date(), 24)) // Last 24 hours
  const [endTime, setEndTime] = useState<Date>(new Date())
  const [tempStartTime, setTempStartTime] = useState<Date>(() => subHours(new Date(), 24))
  const [tempEndTime, setTempEndTime] = useState<Date>(new Date())
  const [showImportModal, setShowImportModal] = useState(false)
  const [anomalyThreshold, setAnomalyThreshold] = useState<number>(15) // Percentage
  const [activeTab, setActiveTab] = useState<'charts' | 'management'>('charts')

  const timePresets = [
    { label: '1 godzina', hours: 1 },
    { label: '6 godzin', hours: 6 },
    { label: '24 godziny', hours: 24 },
    { label: '7 dni', days: 7 },
  ]

  useEffect(() => {
    loadObjectsAndReadings()
  }, [startTime, endTime])

  const loadObjectsAndReadings = async () => {
    setLoading(true)
    try {
      // Load objects that have live readings
      const objectsResponse = await fetch(
        `/api/statistics/objects?start_date=${startTime.toISOString()}&end_date=${endTime.toISOString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )

      if (objectsResponse.ok) {
        const objectsData = await objectsResponse.json()
        // Filter objects that have infrastructure types supporting readings
        const filteredObjects = objectsData.filter((obj: InfrastructureObject) =>
          obj.infrastructure_type === 'Stacja Uzdatniania wody' ||
          obj.infrastructure_type === 'Hydroforownia'
        )
        setObjects(filteredObjects)

        // Auto-select ALL objects that have readings in this time range
        if (filteredObjects.length > 0 && selectedObjects.length === 0) {
          const allObjectIds = filteredObjects.map((obj: InfrastructureObject) => obj.id)
          setSelectedObjects(allObjectIds)
        }
      }

      // Load live readings
      await loadLiveReadings()
    } catch (error) {
      console.error('Error loading data:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadLiveReadings = async (objectIds?: number[]) => {
    const ids = objectIds || selectedObjects
    if (ids.length === 0) {
      setLiveReadings([])
      return
    }

    try {
      const params = new URLSearchParams({
        start_date: startTime.toISOString(),
        end_date: endTime.toISOString(),
      })

      ids.forEach(id => params.append('object_ids', id.toString()))

      const response = await fetch(`/api/statistics/live-readings?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      })

      if (response.ok) {
        const data = await response.json()
        setLiveReadings(data)
      }
    } catch (error) {
      console.error('Error loading live readings:', error)
    }
  }

  const handleTimePreset = (hours?: number, days?: number) => {
    const now = new Date()
    let newStartTime = now

    if (hours) {
      newStartTime = subHours(now, hours)
    } else if (days) {
      newStartTime = subDays(now, days)
    }

    setStartTime(newStartTime)
    setEndTime(now)
    setTempStartTime(newStartTime)
    setTempEndTime(now)
  }

  const handleApplyCustomFilters = () => {
    setStartTime(tempStartTime)
    setEndTime(tempEndTime)
  }

  const handleObjectToggle = (objectId: number) => {
    const newSelected = selectedObjects.includes(objectId)
      ? selectedObjects.filter(id => id !== objectId)
      : [...selectedObjects, objectId]

    setSelectedObjects(newSelected)
    loadLiveReadings(newSelected)
  }

  const handleImportSuccess = () => {
    setShowImportModal(false)
    loadObjectsAndReadings()
  }

  // Analyze trends and detect anomalies in flow rates
  const analyzeTrends = (readings: LiveReading[]) => {
    if (readings.length < 3) return []

    // Group by object and reading type
    const trends = readings.reduce((acc, reading) => {
      const key = `${reading.object_id}-${reading.reading_type}`
      if (!acc[key]) acc[key] = []
      acc[key].push(reading)
      return acc
    }, {} as { [key: string]: LiveReading[] })

    const anomalies: Anomaly[] = []

    Object.entries(trends).forEach(([key, readings]) => {
      if (readings.length < 3) return

      // Sort by timestamp
      readings.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())

      // Calculate flow rates using 10-minute intervals
      const flows: { flow: number, reading: LiveReading }[] = []
      const minIntervalMs = 8 * 60 * 1000  // 8 minutes
      const maxIntervalMs = 12 * 60 * 1000 // 12 minutes

      for (let i = 1; i < readings.length; i++) {
        const currentReading = readings[i]
        const currentTime = new Date(currentReading.timestamp).getTime()

        // Find the most recent reading within 8-12 minutes before this one
        let prevIndex = -1
        for (let j = i - 1; j >= 0; j--) {
          const prevTime = new Date(readings[j].timestamp).getTime()
          const timeDiff = currentTime - prevTime

          if (timeDiff >= minIntervalMs && timeDiff <= maxIntervalMs) {
            prevIndex = j
            break
          } else if (timeDiff > maxIntervalMs) {
            break // Too far back, stop searching
          }
        }

        if (prevIndex !== -1) {
          const prevReading = readings[prevIndex]
          const timeDiff = (currentTime - new Date(prevReading.timestamp).getTime()) / (1000 * 60 * 60) // hours
          const valueDiff = currentReading.value - prevReading.value

          // Calculate flow rate - values are in liters, convert to m³/h
          const flow = timeDiff > 0 ? (valueDiff / 1000) / timeDiff : 0

          if (flow >= -0.0001) { // Include positive flow and small measurement errors
            flows.push({
              flow: Math.max(0, flow),
              reading: currentReading
            })
          }
        }
      }

      if (flows.length < 2) return

      // Calculate average and std dev of flow rates
      const flowValues = flows.map(f => f.flow)
      const avgFlow = flowValues.reduce((a, b) => a + b, 0) / flowValues.length
      const variance = flowValues.reduce((acc, val) => acc + Math.pow(val - avgFlow, 2), 0) / flowValues.length
      const stdDev = Math.sqrt(variance)

      // Detect anomalies in flow rates
      const threshold = anomalyThreshold / 100
      flows.forEach(({ flow, reading }) => {
        const deviation = Math.abs(flow - avgFlow) / avgFlow
        if (deviation > threshold) {
          anomalies.push({
            ...reading,
            deviation: deviation * 100,
            trend: flow > avgFlow ? 'above' : 'below'
          })
        }
      })
    })

    return anomalies
  }

  const anomalies = analyzeTrends(liveReadings)

  // Group readings by object
  const readingsByObject = liveReadings.reduce((acc, reading) => {
    if (!acc[reading.object_id]) {
      acc[reading.object_id] = []
    }
    acc[reading.object_id].push(reading)
    return acc
  }, {} as { [key: number]: LiveReading[] })

  const selectedObjectsData = objects.filter(obj => selectedObjects.includes(obj.id))

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        <span className="ml-2">Ładowanie danych Live...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <h2 className="text-2xl font-bold text-gray-900">📊 Statystyki Live</h2>
          
          {/* Tab navigation */}
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('charts')}
              className={`px-4 py-2 text-sm font-medium ${
                activeTab === 'charts'
                  ? 'border-b-2 border-purple-500 text-purple-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Wykresy
            </button>
            <button
              onClick={() => setActiveTab('management')}
              className={`px-4 py-2 text-sm font-medium ${
                activeTab === 'management'
                  ? 'border-b-2 border-purple-500 text-purple-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Zarządzanie danymi
            </button>
          </div>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => setShowImportModal(true)}
            className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md font-medium flex items-center"
          >
            <span className="mr-2">📁</span>
            Import CSV
          </button>
          <div className="text-sm text-gray-600 self-center">
            {anomalies.length > 0 && (
              <span className="text-red-600 font-medium">
                ⚠️ Wykryto {anomalies.length} anomali(anomalii)
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Management Tab */}
      {activeTab === 'management' && (
        <LiveReadingsManagementTable token={token} />
      )}

      {/* Charts Tab */}
      {activeTab === 'charts' && (
        <>
          {/* Time range controls */}
          <div className="bg-white p-6 rounded-lg shadow">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Zakres czasowy
            </label>
            <div className="flex gap-2 mb-4">
              {timePresets.map(preset => (
                <button
                  key={preset.label}
                  onClick={() => handleTimePreset(preset.hours, preset.days)}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                    preset.hours === 24 && startTime.getTime() === subHours(new Date(), 24).getTime()
                      ? 'bg-purple-100 text-purple-700 border-2 border-purple-200'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>

          {/* Custom time range */}
          <div>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Od
                </label>
                <input
                  type="datetime-local"
                  value={format(tempStartTime, "yyyy-MM-dd'T'HH:mm")}
                  onChange={(e) => setTempStartTime(new Date(e.target.value))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Do
                </label>
                <input
                  type="datetime-local"
                  value={format(tempEndTime, "yyyy-MM-dd'T'HH:mm")}
                  onChange={(e) => setTempEndTime(new Date(e.target.value))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>
            <button
              onClick={handleApplyCustomFilters}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md font-medium"
            >
              Zastosuj filtr
            </button>
          </div>

          {/* Object selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Wybrane SUW/Hydroforownie ({selectedObjects.length} z {objects.length})
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-48 overflow-y-auto">
              {objects.map(obj => (
                <label key={obj.id} className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                  <input
                    type="checkbox"
                    checked={selectedObjects.includes(obj.id)}
                    onChange={() => handleObjectToggle(obj.id)}
                    className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-sm">{obj.name}</div>
                    <div className="text-xs text-gray-500">
                      {obj.infrastructure_type} • ⏰ czas rzeczywisty
                    </div>
                  </div>
                </label>
              ))}
            </div>
            {objects.length === 0 && (
              <div className="text-center py-4 text-gray-500">
                Brak danych live w wybranym zakresie czasowym
              </div>
            )}
          </div>

          {/* Anomaly threshold */}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">
                Próg wykrywania anomalii: {anomalyThreshold}% odchylenia
              </label>
              <input
                type="range"
                min="5"
                max="50"
                step="1"
                value={anomalyThreshold}
                onChange={(e) => setAnomalyThreshold(Number(e.target.value))}
                className="w-32"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Przepływy odchylające się o więcej niż {anomalyThreshold}% od średniej zostaną oznaczone jako anomalie
            </p>
          </div>
        </div>
      </div>

      {/* Anomaly alert */}
      {anomalies.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                ⚠️ Wykryto anomalie w danych!
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <ul className="list-disc pl-5 space-y-1">
                  {anomalies.slice(0, 5).map((anomaly, index) => (
                    <li key={index}>
                      {anomaly.object_name} - {anomaly.reading_type_label}: {(anomaly.value / 1000).toFixed(2)} m³
                      ({anomaly.deviation.toFixed(1)}% {anomaly.trend === 'above' ? 'powyżej' : 'poniżej'} średniej)
                      <span className="text-xs ml-2">
                        {format(new Date(anomaly.timestamp), 'dd.MM.yyyy HH:mm')}
                      </span>
                    </li>
                  ))}
                </ul>
                {anomalies.length > 5 && (
                  <p className="mt-2 text-xs">
                    ...i {anomalies.length - 5} innych anomalii
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Charts */}
      {selectedObjectsData.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          Wybierz przynajmniej jeden obiekt infrastruktury do wyświetlenia wykresów Live
        </div>
      ) : (
        <div className="space-y-8">
          {selectedObjectsData.map(obj => (
            <LiveChart
              key={obj.id}
              object={obj}
              readings={readingsByObject[obj.id] || []}
              anomalies={anomalies.filter(a => a.object_id === obj.id)}
              startTime={startTime}
              endTime={endTime}
            />
          ))}
        </div>
      )}

        </>
      )}

      {/* Import Modal */}
      <LiveImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        token={token}
        onImportSuccess={handleImportSuccess}
      />
    </div>
  )
}
